/**
 * GymRequestForm Component
 *
 * Form component for creating and editing gym requests.
 * Includes validation using the shared schema and calls Firebase callable functions.
 */

import { useState, type FormEvent, type ChangeEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '@/lib/firebase';
import { createGymRequestSchema } from '@flash/shared';
import type { GymRequestCreateData } from '@flash/shared';

/**
 * Props for GymRequestForm component
 */
export interface GymRequestFormProps {
  /** Mode of the form - create new or edit existing */
  mode: 'create' | 'edit';
  /** Initial data for edit mode */
  initialData?: Partial<GymRequestCreateData>;
  /** Request ID for edit mode */
  requestId?: string;
  /** Callback when form is successfully submitted */
  onSuccess: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Callback when cancel button is clicked */
  onCancel?: () => void;
}

/**
 * Form field state
 */
interface FormState {
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  country: string;
}

/**
 * Field error state
 */
interface FieldErrors {
  name?: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
}

/**
 * GymRequestForm for creating or editing gym requests
 *
 * @example
 * ```tsx
 * // Create mode
 * <GymRequestForm mode="create" onSuccess={() => navigate('/gym-requests')} />
 *
 * // Edit mode
 * <GymRequestForm
 *   mode="edit"
 *   initialData={existingRequest}
 *   requestId={requestId}
 *   onSuccess={() => navigate('/gym-requests')}
 * />
 * ```
 */
export const GymRequestForm = ({
  mode,
  initialData,
  requestId,
  onSuccess,
  onError,
  onCancel,
}: GymRequestFormProps) => {
  const [formData, setFormData] = useState<FormState>({
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    address: initialData?.address ?? '',
    city: initialData?.city ?? '',
    state: initialData?.state ?? '',
    country: initialData?.country ?? '',
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle input change
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (fieldErrors[name as keyof FieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    // Clear general error
    if (error) {
      setError(null);
    }
  };

  /**
   * Validate form data using Zod schema
   */
  const validateForm = (): boolean => {
    const result = createGymRequestSchema.safeParse(formData);

    if (!result.success) {
      const errors: FieldErrors = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof FieldErrors;
        if (field && !errors[field]) {
          errors[field] = err.message;
        }
      });
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const functions = getFirebaseFunctions();

      if (mode === 'create') {
        // Create new gym request
        const createGymRequest = httpsCallable<
          GymRequestCreateData,
          { success: boolean; requestId: string }
        >(functions, 'createGymRequest');

        await createGymRequest(formData);
      } else {
        // Update existing gym request
        if (!requestId) {
          throw new Error('Request ID is required for edit mode');
        }

        const updateGymRequest = httpsCallable<
          { requestId: string } & Partial<GymRequestCreateData>,
          { success: boolean }
        >(functions, 'updateGymRequest');

        await updateGymRequest({
          requestId,
          ...formData,
        });
      }

      onSuccess();
    } catch (err) {
      const error = err as { code?: string; message?: string };
      let errorMessage = `Failed to ${mode === 'create' ? 'create' : 'update'} gym request. Please try again.`;

      // Handle specific error codes
      if (error.code === 'functions/unauthenticated') {
        errorMessage = 'You must be logged in to submit a gym request.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message ?? 'Invalid request data. Please check your inputs.';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to update this request.';
      } else if (error.code === 'functions/failed-precondition') {
        errorMessage = 'This request cannot be updated in its current state.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Gym request not found.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      onError?.(new Error(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if form has changes (for edit mode)
   */
  const hasChanges = (): boolean => {
    if (mode === 'create') return true;
    return (
      formData.name !== (initialData?.name ?? '') ||
      formData.description !== (initialData?.description ?? '') ||
      formData.address !== (initialData?.address ?? '') ||
      formData.city !== (initialData?.city ?? '') ||
      formData.state !== (initialData?.state ?? '') ||
      formData.country !== (initialData?.country ?? '')
    );
  };

  const isSubmitDisabled = loading || (mode === 'edit' && !hasChanges());

  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      {/* Name Field */}
      <div style={fieldStyle}>
        <label htmlFor="name" style={labelStyle}>
          Gym Name <span style={requiredStyle}>*</span>
        </label>
        <input
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter gym name"
          disabled={loading}
          style={{
            ...inputStyle,
            ...(fieldErrors.name ? inputErrorStyle : {}),
          }}
        />
        {fieldErrors.name && <p style={fieldErrorStyle}>{fieldErrors.name}</p>}
      </div>

      {/* Description Field */}
      <div style={fieldStyle}>
        <label htmlFor="description" style={labelStyle}>
          Description <span style={requiredStyle}>*</span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Describe the gym (facilities, features, etc.)"
          disabled={loading}
          rows={4}
          style={{
            ...inputStyle,
            ...textareaStyle,
            ...(fieldErrors.description ? inputErrorStyle : {}),
          }}
        />
        {fieldErrors.description && <p style={fieldErrorStyle}>{fieldErrors.description}</p>}
        <p style={hintStyle}>Minimum 10 characters</p>
      </div>

      {/* Address Field */}
      <div style={fieldStyle}>
        <label htmlFor="address" style={labelStyle}>
          Street Address <span style={requiredStyle}>*</span>
        </label>
        <input
          id="address"
          name="address"
          type="text"
          value={formData.address}
          onChange={handleChange}
          placeholder="123 Main Street"
          disabled={loading}
          style={{
            ...inputStyle,
            ...(fieldErrors.address ? inputErrorStyle : {}),
          }}
        />
        {fieldErrors.address && <p style={fieldErrorStyle}>{fieldErrors.address}</p>}
      </div>

      {/* City and State Row */}
      <div style={rowStyle}>
        <div style={{ ...fieldStyle, flex: 1 }}>
          <label htmlFor="city" style={labelStyle}>
            City <span style={requiredStyle}>*</span>
          </label>
          <input
            id="city"
            name="city"
            type="text"
            value={formData.city}
            onChange={handleChange}
            placeholder="City"
            disabled={loading}
            style={{
              ...inputStyle,
              ...(fieldErrors.city ? inputErrorStyle : {}),
            }}
          />
          {fieldErrors.city && <p style={fieldErrorStyle}>{fieldErrors.city}</p>}
        </div>

        <div style={{ ...fieldStyle, flex: 1 }}>
          <label htmlFor="state" style={labelStyle}>
            State/Province <span style={requiredStyle}>*</span>
          </label>
          <input
            id="state"
            name="state"
            type="text"
            value={formData.state}
            onChange={handleChange}
            placeholder="State or Province"
            disabled={loading}
            style={{
              ...inputStyle,
              ...(fieldErrors.state ? inputErrorStyle : {}),
            }}
          />
          {fieldErrors.state && <p style={fieldErrorStyle}>{fieldErrors.state}</p>}
        </div>
      </div>

      {/* Country Field */}
      <div style={fieldStyle}>
        <label htmlFor="country" style={labelStyle}>
          Country <span style={requiredStyle}>*</span>
        </label>
        <input
          id="country"
          name="country"
          type="text"
          value={formData.country}
          onChange={handleChange}
          placeholder="Country"
          disabled={loading}
          style={{
            ...inputStyle,
            ...(fieldErrors.country ? inputErrorStyle : {}),
          }}
        />
        {fieldErrors.country && <p style={fieldErrorStyle}>{fieldErrors.country}</p>}
      </div>

      {/* General Error Message */}
      {error && (
        <div style={errorContainerStyle}>
          <p style={errorMessageStyle}>{error}</p>
        </div>
      )}

      {/* Form Actions */}
      <div style={actionsStyle}>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={loading} style={cancelButtonStyle}>
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitDisabled}
          style={submitButtonStyle(isSubmitDisabled)}
        >
          {loading ? (
            <>
              <span style={spinnerStyle} />
              {mode === 'create' ? 'Submitting...' : 'Updating...'}
            </>
          ) : mode === 'create' ? (
            'Submit Request'
          ) : (
            'Update Request'
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
};

/**
 * Component styles
 */
import type { CSSProperties } from 'react';

const formStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const fieldStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  gap: '16px',
};

const labelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#374151',
};

const requiredStyle: CSSProperties = {
  color: '#ef4444',
};

const inputStyle: CSSProperties = {
  padding: '12px',
  fontSize: '16px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const inputErrorStyle: CSSProperties = {
  borderColor: '#ef4444',
  borderWidth: '2px',
};

const textareaStyle: CSSProperties = {
  resize: 'vertical',
  minHeight: '100px',
  fontFamily: 'inherit',
};

const fieldErrorStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.75rem',
  color: '#dc2626',
};

const hintStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.75rem',
  color: '#6b7280',
};

const errorContainerStyle: CSSProperties = {
  padding: '12px 16px',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
};

const errorMessageStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: '#dc2626',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '8px',
};

const cancelButtonStyle: CSSProperties = {
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 500,
  backgroundColor: '#ffffff',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const submitButtonStyle = (disabled: boolean): CSSProperties => ({
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  backgroundColor: disabled ? '#9ca3af' : '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'background-color 0.2s',
});

const spinnerStyle: CSSProperties = {
  display: 'inline-block',
  width: '16px',
  height: '16px',
  border: '2px solid white',
  borderTopColor: 'transparent',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

export default GymRequestForm;
